/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Accordion from "./Accordion";
import { PrintSettingsStateTypes } from "../types/print_settings";
import { savePrintSettings } from "../services/api";

interface PrintSettingsProps {
  printSettings: PrintSettingsStateTypes;
  fetchPrintSettings: () => void;
}

const printLineSettings = {
  "1": {
    printPerLine: 1,
    pageWidth: 10,
    pageHeight: 4,
    fontSize: 12,
    barcodeWidth: 1,
    barcodeHeight: 40,
  },
  "2": {
    printPerLine: 2,
    pageWidth: 15,
    pageHeight: 5,
    fontSize: 16,
    barcodeWidth: 2,
    barcodeHeight: 60,
  },
  "3": {
    printPerLine: 3,
    pageWidth: 20,
    pageHeight: 6,
    fontSize: 20,
    barcodeWidth: 3,
    barcodeHeight: 80,
  },
};

const PrintSettings: React.FC<PrintSettingsProps> = ({
  printSettings,
  fetchPrintSettings,
}) => {
  const [settings, setSettings] = useState(printSettings);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (JSON.stringify(settings) !== JSON.stringify(printSettings)) {
      setSettings(printSettings);
    }
  }, [printSettings]);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, type, value } = e.target;
    const newValue =
      type === "checkbox"
        ? e.target.checked
        : type === "number"
          ? value === ""
            ? ""
            : Number(value)
          : value;

    if (name === "printLine") {
      setIsSubmitting(true);
      let updatedSettings = {
        ...settings,
        printLine: value,
      };
      const printLineValue = value.includes("1")
        ? "1"
        : value.includes("2")
          ? "2"
          : "3";
      updatedSettings = Object.assign(
        {},
        updatedSettings,
        printLineSettings[printLineValue],
      );
      setSettings(updatedSettings);
      setTimeout(() => handleSave(updatedSettings), 1000);
    } else {
      setSettings((prev) => ({ ...prev, [name]: newValue }));
    }
  };

  const handleSave = async (newSettings?: PrintSettingsStateTypes) => {
    try {
      setIsSubmitting(true);
      const finalSettings = newSettings || settings;
      const data = await savePrintSettings(finalSettings);
      if (data) {
        toast.success("Print settings saved successfully");
        fetchPrintSettings();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save print settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  type NumberFieldKeys = Exclude<
    {
      [K in keyof PrintSettingsStateTypes]: PrintSettingsStateTypes[K] extends number
        ? K
        : never;
    }[keyof PrintSettingsStateTypes],
    undefined
  >;

  const numberFields: { label: string; name: NumberFieldKeys }[] = [
    { label: "Print Per Line", name: "printPerLine" },
    { label: "Barcode Width Per Bar", name: "barcodeWidth" },
    { label: "Barcode Height", name: "barcodeHeight" },
    { label: "Page Width (cm)", name: "pageWidth" },
    { label: "Page Height (cm)", name: "pageHeight" },
    { label: "Font Size", name: "fontSize" },
    { label: "Font Price Size", name: "fontPrice" },
    { label: "Margin Top (mm)", name: "marginTop" },
    { label: "Margin Bottom (mm)", name: "marginBottom" },
    { label: "Margin Left (mm)", name: "marginLeft" },
    { label: "Margin Right (mm)", name: "marginRight" },
  ];

  return (
    <Accordion
      title="Print Settings"
      content={
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Print Line Preset */}
            <div className="flex items-center">
              <label className="w-1/2">Printline Preset</label>
              <select
                name="printLine"
                value={settings.printLine || 1}
                onChange={handleChange}
                className="w-1/2 border p-2 rounded"
              >
                {[1, 2, 3].map((line) => (
                  <option key={line} value={line}>
                    Print Line {line}
                  </option>
                ))}
              </select>
            </div>

            {numberFields.map(({ label, name }) => (
              <div key={name} className="flex items-center">
                <label className="w-1/2">{label}</label>
                <input
                  type="number"
                  name={name}
                  value={settings[name]}
                  readOnly={name === "printPerLine"}
                  onChange={handleChange}
                  onFocus={(e) => {
                    if (e.target.value === "0") {
                      e.target.select();
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      setSettings((prev) => ({
                        ...prev,
                        [name]: 0,
                      }));
                    }
                  }}
                  className="w-1/2 border p-2 rounded"
                />
              </div>
            ))}

            {/* Barcode Type */}
            <div className="flex items-center">
              <label className="w-1/2">Barcode Type</label>
              <select
                name="barcodeType"
                value={settings.barcodeType || "CODE128"}
                onChange={handleChange}
                className="w-1/2 border p-2 rounded"
              >
                {["CODE128", "EAN13", "UPC", "QR"].map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <label className="w-1/2">Group Labels</label>
              <select
                name="printGrouping"
                value={settings.printGrouping || "item"}
                onChange={handleChange}
                className="w-1/2 border p-2 rounded"
              >
                <option value="item">Per Item</option>
                <option value="row">Per Row</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Custom Date */}
            {settings.showDate && (
              <>
                <div className="flex items-center">
                  <label className="w-1/2">Date</label>
                  <input
                    type="date"
                    name="customDate"
                    value={settings.customDate || ""}
                    onChange={handleChange}
                    className="w-1/2 border p-2 rounded"
                  />
                </div>
                <div className="flex items-center">
                  <label className="w-1/2">Date Format</label>
                  <select
                    name="dateFormat"
                    value={settings.dateFormat || "DD MMM YYYY"}
                    onChange={handleChange}
                    className="w-1/2 border p-2 rounded"
                  >
                    {[
                      "DD MMM YYYY",
                      "YYYY MMM DD",
                      "DD/MM/YYYY",
                      "DD/MM/YY",
                      "YYMMDD",
                      "YYYY-MM-DD",
                    ].map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Save Button */}
          <div className="text-right mt-4">
            <button
              onClick={() => handleSave()}
              disabled={isSubmitting}
              className={`px-6 py-2 rounded ${
                isSubmitting ? "bg-gray-400" : "bg-blue-500 hover:bg-blue-600"
              } text-white`}
            >
              {isSubmitting ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      }
    />
  );
};

export default PrintSettings;
